/**
 * The indexable public pages, shared by the app (head tags, pre-render) and the Worker
 * (sitemap.xml). Everything else is noindex and disallowed in robots.txt.
 */
export const SITE_ORIGIN = "https://cafonapay.com";

export interface PublicPath {
  /** Path without a language prefix. */
  path: string;
  /** Also published in French under /fr. */
  bilingual: boolean;
  priority: number;
  changefreq: "weekly" | "monthly" | "yearly";
}

export const PUBLIC_PATHS: PublicPath[] = [
  { path: "/", bilingual: true, priority: 1, changefreq: "weekly" },
  { path: "/developers", bilingual: false, priority: 0.6, changefreq: "monthly" },
  { path: "/verify", bilingual: true, priority: 0.5, changefreq: "monthly" },
  { path: "/legal/terms", bilingual: true, priority: 0.3, changefreq: "yearly" },
  { path: "/legal/privacy", bilingual: true, priority: 0.3, changefreq: "yearly" },
];

export const frenchPath = (path: string) => (path === "/" ? "/fr" : `/fr${path}`);

/** Paths crawlers must not visit: signed-in app, API, payment and invitation links, personal pages. */
export const DISALLOWED_PREFIXES = ["/app/", "/api/", "/checkout/", "/pay/", "/join/", "/c/", "/verify/", "/fr/verify/", "/login", "/signup"];

export function robotsTxt(production: boolean): string {
  if (!production) return "# Not the production site: nothing to index here.\nUser-agent: *\nDisallow: /\n";
  return [
    "User-agent: *",
    "Allow: /",
    ...DISALLOWED_PREFIXES.map((p) => `Disallow: ${p}`),
    "",
    `Sitemap: ${SITE_ORIGIN}/sitemap.xml`,
    "",
  ].join("\n");
}

export function sitemapXml(lastmod: string): string {
  const url = (loc: string, p: PublicPath, alternates: boolean) =>
    [
      "  <url>",
      `    <loc>${SITE_ORIGIN}${loc}</loc>`,
      `    <lastmod>${lastmod}</lastmod>`,
      `    <changefreq>${p.changefreq}</changefreq>`,
      `    <priority>${p.priority.toFixed(1)}</priority>`,
      ...(alternates
        ? [
            `    <xhtml:link rel="alternate" hreflang="en" href="${SITE_ORIGIN}${p.path}"/>`,
            `    <xhtml:link rel="alternate" hreflang="fr" href="${SITE_ORIGIN}${frenchPath(p.path)}"/>`,
            `    <xhtml:link rel="alternate" hreflang="x-default" href="${SITE_ORIGIN}${p.path}"/>`,
          ]
        : []),
      "  </url>",
    ].join("\n");
  const urls = PUBLIC_PATHS.flatMap((p) => [url(p.path, p, p.bilingual), ...(p.bilingual ? [url(frenchPath(p.path), p, true)] : [])]);
  return `<?xml version="1.0" encoding="UTF-8"?>\n<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9" xmlns:xhtml="http://www.w3.org/1999/xhtml">\n${urls.join("\n")}\n</urlset>\n`;
}

/** Every indexable URL (for IndexNow submissions). */
export const allPublicUrls = () => PUBLIC_PATHS.flatMap((p) => [`${SITE_ORIGIN}${p.path}`, ...(p.bilingual ? [`${SITE_ORIGIN}${frenchPath(p.path)}`] : [])]);
